<template>
  <div class="rcs-visual-image-card">
    <div
      class="rcs-visual-image-card__preview"
      :class="[previewClass, { 'rcs-visual-image-card__preview--has-image': previewVisible }]"
    >
      <img
        v-if="hasImage && !previewFailed"
        :key="previewImageKey"
        :src="previewImageSrc"
        alt=""
        @error="onPreviewError"
      >
    </div>
    <div class="rcs-visual-image-card__content">
      <label :for="fieldId">
        {{ fieldLabel }}
      </label>
      <div class="rcs-visual-image-field__actions">
        <Button
          type="button"
          :label="chooseButtonLabel"
          icon="far fa-images"
          outlined
          :disabled="checkingImage"
          :loading="checkingImage"
          @click="openFileManager"
        />
        <Button
          v-if="hasImage"
          type="button"
          :label="t('storeParameters.services.rcsServiceParameter.wizard.visual.actions.recropImage')"
          icon="far fa-crop-simple"
          outlined
          @click="openRecrop"
        />
      </div>
      <InlineMessage
        v-if="fieldError"
        severity="error"
        class="rcs-visual-image-field__error"
      >
        {{ fieldError }}
      </InlineMessage>
      <ul class="rcs-visual-image-card__specs">
        <li
          v-for="(specKey, index) in specKeys"
          :key="index"
        >
          {{ t(specKey) }}
        </li>
      </ul>
    </div>

    <FileManager
      v-if="displayFileManager"
      :display-dialog="displayFileManager"
      extensions="image/*"
      @on-close-dialog="displayFileManager = false"
      @on-choose-file="onChooseFile"
    />

    <RcsVisualImageCropModal
      v-if="displayCropModal"
      :display-dialog="displayCropModal"
      :image-url="cropSourceUrl"
      :preset="preset"
      @on-close-dialog="closeCropModal"
      @on-save="onCropSaved"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import InlineMessage from 'primevue/inlinemessage';
import FileManager from '@/components/file-manager/FileManager.vue';
import RcsVisualImageCropModal from '@/components/rcs-onboarding/RcsVisualImageCropModal.vue';
import type { File as MediaFile } from '@/types/field-types';
import {
  canUseRcsImageWithoutCrop,
  getRcsVisualPreviewUrl,
} from '@/utils/rcs-visual-image';
import type { RcsVisualImagePreset } from '@/utils/rcs-visual-image';

export default defineComponent({
  name: 'RcsVisualImageField',

  components: {
    Button,
    InlineMessage,
    FileManager,
    RcsVisualImageCropModal,
  },

  props: {
    modelValue: {
      type: String,
      default: '',
    },

    preset: {
      type: String as PropType<RcsVisualImagePreset>,
      required: true,
    },

    fieldId: {
      type: String,
      required: true,
    },
  },

  emits: ['update:modelValue', 'operation-failed'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const displayFileManager = ref(false);
    const displayCropModal = ref(false);
    const cropSourceUrl = ref('');
    const previewFailed = ref(false);
    const previewUseResizeProxy = ref(false);
    const fieldError = ref('');
    const checkingImage = ref(false);

    const hasImage = computed(() => !!props.modelValue?.trim());

    const previewVisible = computed(
      () => hasImage.value && !previewFailed.value,
    );

    const previewImageSrc = computed(() => {
      if (!hasImage.value) {
        return '';
      }
      if (previewUseResizeProxy.value) {
        return getRcsVisualPreviewUrl(props.modelValue, props.preset);
      }
      return props.modelValue;
    });

    const previewImageKey = computed(
      () => `${props.preset}-${props.modelValue}-${previewUseResizeProxy.value}`,
    );

    const previewClass = computed(() => (
      props.preset === 'logo'
        ? 'rcs-visual-image-card__preview--avatar'
        : 'rcs-visual-image-card__preview--banner'
    ));

    const fieldLabel = computed(() => (
      props.preset === 'logo'
        ? t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.logoImage')
        : t('storeParameters.services.rcsServiceParameter.wizard.visual.fields.bannerImage')
    ));

    const chooseButtonLabel = computed(() => (
      hasImage.value
        ? t('storeParameters.services.rcsServiceParameter.wizard.visual.actions.replaceImage')
        : t('storeParameters.services.rcsServiceParameter.wizard.visual.actions.chooseImage')
    ));

    const specKeys = computed(() => {
      const base = props.preset === 'logo'
        ? [
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.logo.maxSize',
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.logo.dimensions',
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.logo.format',
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.logo.cropNote',
        ]
        : [
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.banner.maxSize',
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.banner.dimensions',
          'storeParameters.services.rcsServiceParameter.wizard.visual.specs.banner.format',
        ];
      return base;
    });

    const setFieldError = (key: string | null) => {
      fieldError.value = key
        ? t(`storeParameters.services.rcsServiceParameter.wizard.visual.errors.${key}`)
        : '';
      emit('operation-failed', !!key);
    };

    const openCropWithUrl = (url: string) => {
      if (!url?.trim()) {
        return;
      }
      cropSourceUrl.value = url;
      displayCropModal.value = true;
    };

    const openFileManager = () => {
      displayFileManager.value = true;
    };

    const openRecrop = () => {
      openCropWithUrl(props.modelValue);
    };

    const closeCropModal = () => {
      displayCropModal.value = false;
    };

    const onChooseFile = async (file: MediaFile) => {
      displayFileManager.value = false;

      if (!file?.url) {
        return;
      }

      setFieldError(null);
      checkingImage.value = true;

      try {
        const useDirectUrl = await canUseRcsImageWithoutCrop(file.url, props.preset);
        if (useDirectUrl) {
          emit('update:modelValue', file.url);
          return;
        }
        openCropWithUrl(file.url);
      } catch {
        openCropWithUrl(file.url);
      } finally {
        checkingImage.value = false;
      }
    };

    const onCropSaved = (url: string) => {
      emit('update:modelValue', url);
      setFieldError(null);
      closeCropModal();
    };

    const onPreviewError = () => {
      if (!previewUseResizeProxy.value && props.modelValue?.includes('media.shopimind.io')) {
        previewUseResizeProxy.value = true;
        return;
      }
      previewFailed.value = true;
    };

    watch(() => props.modelValue, () => {
      previewFailed.value = false;
      previewUseResizeProxy.value = false;
    });

    return {
      t,
      displayFileManager,
      displayCropModal,
      cropSourceUrl,
      fieldError,
      checkingImage,
      hasImage,
      previewVisible,
      previewImageSrc,
      previewImageKey,
      previewClass,
      fieldLabel,
      chooseButtonLabel,
      specKeys,
      openFileManager,
      openRecrop,
      closeCropModal,
      onChooseFile,
      onCropSaved,
      onPreviewError,
    };
  },
});
</script>
